import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { PRIVATE, PUBLIC } from 'router/routes';

import { getToken, removeTokens, setTokenAccess, setWellFunnelToken } from 'api/utils/tokenManagement';

import {
    authenticateByCWTToken,
    authentication,
    logout,
    setAuthenticationStatus,
    setAuthRedirectUrl,
} from 'store/auth/actions';
import { getAuthWellFunnelToken } from 'store/auth/accessors';

import { AUTH_TOKEN_PARAM_NAME, USER_SOURCE } from 'constants/urlParams';
import { AUTH_TOKEN__INVALID_JWT_FORMAT } from 'constants/dataDog';
import { AUTH_TOKEN_NAME } from 'constants/api';

import { initHotjar } from 'services/Hotjar';
import { trackDatadogRumAction } from 'services/DataDog';
import { initAnalytics } from 'services/Analytics/initAnalytics';
import { Provider } from 'services/Analytics/core';
import {
    sendAnalyticLoginWithTokenError,
    sendAnalyticLoginWithTokenStart,
    sendAnalyticLoginWithTokenSuccess,
} from 'services/Analytics';

import useModals from 'modals/hooks';
import { useDispatch } from 'hooks/store';
import { getAllowedAuthQueryParams } from 'hooks/common/helpers';

import { LS_KEYS } from 'helpers/localStorage';
import { isJwtFormat, isNewTokenType } from 'helpers/api';
import isPrivateRoute from '../helpers/isPrivateRoute';

import { AccountTab } from 'pages/account/types';

import { ModalName } from 'modals/types';

export const useAuthentication = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { search, pathname } = useLocation();
    const { openModal } = useModals();

    const allowedQueryParams = getAllowedAuthQueryParams(search);

    const isPrivate = isPrivateRoute(pathname);
    const pathWithAllowedQuery = `${pathname}${allowedQueryParams}`;

    const isBigScreen = pathWithAllowedQuery.includes('cast');

    const params = new URLSearchParams(search);
    const userSource = params.get(USER_SOURCE);

    if (userSource) {
        sessionStorage.setItem(USER_SOURCE, userSource);
        Provider.setUserSource(userSource);
    }

    useEffect(() => {
        const authToken = params.get(AUTH_TOKEN_PARAM_NAME);

        initAnalytics();
        initHotjar();

        if (authToken && authToken !== 'null') {
            const redirectPath = isPrivate
                ? pathWithAllowedQuery
                : `${PRIVATE.ACCOUNT_DEFAULT.path}/${AccountTab.Subscription}`;

            dispatch(setAuthRedirectUrl(redirectPath));

            dispatch(
                authenticateByCWTToken({
                    authToken,
                    onSuccess: () => {
                        navigate(redirectPath);
                    },
                    onError: (error: any) => {
                        removeTokens();
                        navigate(PUBLIC.LOGIN.path);

                        if (error?.response?.status === 403) {
                            openModal(ModalName.NoLogInModal);
                        }
                    },
                })
            );

            return;
        }

        const tokenFromUrl = params.get(AUTH_TOKEN_NAME);

        const isTokenFromUrlValid = isJwtFormat(tokenFromUrl);

        if (tokenFromUrl && !isTokenFromUrlValid) {
            trackDatadogRumAction(AUTH_TOKEN__INVALID_JWT_FORMAT);

            dispatch(logout());

            return;
        }

        if (isTokenFromUrlValid && tokenFromUrl) {
            // TODO: Temporary analytics event to track how many users successfully transition from the onboarding to the cabinet
            sendAnalyticLoginWithTokenStart();

            // Temporary solution, would be removed in the:
            // [YG-Chin-2881] Delete backdoor for exchanging an old token into access/refresh pair on WEB API
            // https://app.asana.com/1/1125283907112431/project/1207601892535016/task/1210057235241791?focus=true
            isNewTokenType() ? setWellFunnelToken(tokenFromUrl) : setTokenAccess(tokenFromUrl);

            localStorage.setItem(LS_KEYS.isUserFirstTimeOnMainPageAfterOB, 'true');
        }

        const accessToken = getToken();
        const authWellFunnelToken = getAuthWellFunnelToken();

        if (!accessToken && !authWellFunnelToken) {
            isPrivate && dispatch(setAuthRedirectUrl(pathWithAllowedQuery));

            dispatch(setAuthenticationStatus(false));

            return;
        }

        dispatch(
            authentication({
                // Temporary solution, would be removed in the:
                // [YG-Chin-2881] Delete backdoor for exchanging an old token into access/refresh pair on WEB API
                // https://app.asana.com/1/1125283907112431/project/1207601892535016/task/1210057235241791?focus=true
                isTokenFromUrl: Boolean(tokenFromUrl),
                isOldTypeToken: !isNewTokenType(),
                onSuccess: () => {
                    // TODO: Temporary analytics event to track how many users successfully transition from the onboarding to the cabinet
                    sendAnalyticLoginWithTokenSuccess();
                    navigate(isPrivate || isBigScreen ? pathWithAllowedQuery : PRIVATE.MAIN.path);
                },
                onError: (message) => {
                    // TODO: Temporary analytics event to track how many users successfully transition from the onboarding to the cabinet
                    sendAnalyticLoginWithTokenError(message);
                    removeTokens();
                    isPrivate && dispatch(setAuthRedirectUrl(pathWithAllowedQuery));
                    navigate(PUBLIC.LOGIN.path);
                },
            })
        );
    }, []);
};
